import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Loader2, Send, LifeBuoy, Handshake, Undo2, Truck, Package,
  CheckCircle2, XCircle, FileText, ExternalLink, ShieldCheck, MessageSquare,
  Sparkles, BadgeEuro, Ban, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import ShipmentTimeline from '../components/ShipmentTimeline';
import {
  API_URL, CASE_STATUS_META, CASE_TYPE_META, PROPOSAL_TYPE_META, REFUND_STATUS_LABELS,
  RETURN_STATUS_LABELS, TIMELINE_META, caseApi, formatDate, formatMoney, isOpenCase,
} from '../lib/orderCases';

const SectionTitle = ({ icon: Icon, children, hint }) => (
  <div className="flex items-start gap-2.5 mb-4">
    <Icon className="w-4 h-4 text-shop-primary mt-0.5" />
    <div>
      <h2 className="text-base md:text-lg font-semibold text-slate-800">{children}</h2>
      {hint && <p className="text-xs text-slate-500 mt-0.5">{hint}</p>}
    </div>
  </div>
);

/* ------------------------------ Proposta ------------------------------ */
const ProposalCard = ({ proposal, isBuyer, onRespond, busy }) => {
  const meta = PROPOSAL_TYPE_META[proposal.type] || PROPOSAL_TYPE_META.OTHER;
  const pending = proposal.status === 'PENDING';
  const statusLabel = {
    PENDING: 'In attesa di risposta', ACCEPTED: 'Accettata',
    REJECTED: 'Non accettata', WITHDRAWN: 'Superata',
  }[proposal.status];

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
      <div
        className={`rounded-2xl p-5 border ${
          pending ? 'border-shop-accent/40 bg-gradient-to-br from-shop-accent/8 to-white shadow-sm' : 'border-slate-200 bg-white'
        }`}
        data-testid={`proposal-${proposal.id}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-shop-accent/15 flex items-center justify-center">
              <meta.Icon className="w-5 h-5 text-[#B8860B]" />
            </div>
            <div>
              <p className="font-semibold text-slate-800">{meta.label}</p>
              <p className="text-xs text-slate-500">
                {proposal.proposer_role === 'SUPERADMIN' ? 'Proposta da Rarifex' : `Proposta da ${proposal.proposer_name}`}
                {' · '}{formatDate(proposal.created_at, true)}
              </p>
            </div>
          </div>
          <Badge className={pending ? 'bg-amber-100 text-amber-800 border-amber-200' : 'bg-slate-100 text-slate-600'}>
            {statusLabel}
          </Badge>
        </div>

        {proposal.refund_amount ? (
          <p className="text-3xl font-bold text-slate-900 mt-4 tracking-tight">
            {formatMoney(proposal.refund_amount)}
          </p>
        ) : null}

        {proposal.message && (
          <p className="text-sm text-slate-600 mt-3 leading-relaxed whitespace-pre-line">{proposal.message}</p>
        )}

        {proposal.requires_return && (
          <p className="text-xs text-teal-700 mt-3 flex items-center gap-1.5">
            <Undo2 className="w-3.5 h-3.5" /> Il rimborso verrà elaborato al rientro del prodotto
          </p>
        )}

        {pending && isBuyer && (
          <div className="flex gap-3 mt-5">
            <Button
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
              onClick={() => onRespond(proposal.id, true)}
              disabled={busy}
              data-testid={`accept-proposal-${proposal.id}`}
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Accetta
            </Button>
            <Button
              variant="outline"
              className="flex-1 border-slate-300"
              onClick={() => onRespond(proposal.id, false)}
              disabled={busy}
              data-testid={`reject-proposal-${proposal.id}`}
            >
              <XCircle className="w-4 h-4 mr-2" /> Rifiuta
            </Button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

/* ------------------------------ Pagina ------------------------------ */
export default function OrderCasePage() {
  const { caseId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  const [proposalOpen, setProposalOpen] = useState(false);
  const [proposalType, setProposalType] = useState('PARTIAL_REFUND');
  const [proposalAmount, setProposalAmount] = useState('');
  const [proposalMessage, setProposalMessage] = useState('');
  const [proposalRequiresReturn, setProposalRequiresReturn] = useState(false);

  const [escalateOpen, setEscalateOpen] = useState(false);
  const [escalateReason, setEscalateReason] = useState('');

  const [returnOpen, setReturnOpen] = useState(false);
  const [carriers, setCarriers] = useState([]);
  const [returnForm, setReturnForm] = useState({ carrier_code: '', tracking_number: '', custom_carrier_name: '', custom_tracking_url: '' });

  const load = useCallback(async () => {
    if (!user?.id || !caseId) return;
    try {
      setData(await caseApi.detail(caseId, user.id));
    } catch (e) {
      toast.error(e.message);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [caseId, user?.id, navigate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!returnOpen || carriers.length) return;
    fetch(`${API_URL}/api/carriers`).then((r) => r.json()).then(setCarriers).catch(() => setCarriers([]));
  }, [returnOpen, carriers.length]);

  const isBuyer = data && user?.id === data.buyer_id;
  const isSeller = data && user?.id === data.seller_id;
  const typeMeta = data ? (CASE_TYPE_META[data.type] || CASE_TYPE_META.OTHER) : null;
  const statusMeta = data ? (CASE_STATUS_META[data.status] || {}) : {};
  const active = data ? isOpenCase(data.status) : false;
  const pendingProposal = useMemo(
    () => (data?.proposals || []).find((p) => p.status === 'PENDING'),
    [data]
  );

  const run = async (fn, successMsg) => {
    setBusy(true);
    try {
      await fn();
      if (successMsg) toast.success(successMsg);
      await load();
    } catch (e) {
      toast.error(e.message);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const handleSend = () => {
    if (!message.trim()) return;
    run(async () => {
      await caseApi.sendMessage(caseId, { sender_id: user.id, content: message.trim() });
      setMessage('');
    }, 'Messaggio inviato');
  };

  const handleProposal = () => {
    const needsAmount = ['PARTIAL_REFUND', 'FULL_REFUND'].includes(proposalType);
    const amount = needsAmount
      ? (proposalType === 'FULL_REFUND' ? data.refundable_amount : parseFloat(proposalAmount))
      : null;
    if (needsAmount && (!amount || amount <= 0)) {
      toast.error('Inserisci un importo valido');
      return;
    }
    if (needsAmount && amount > data.refundable_amount + 0.001) {
      toast.error(`Il massimo rimborsabile è ${formatMoney(data.refundable_amount)}`);
      return;
    }
    run(async () => {
      await caseApi.createProposal(caseId, {
        proposed_by: user.id,
        proposer_role: 'SELLER',
        type: proposalType,
        refund_amount: amount,
        requires_return: proposalRequiresReturn,
        message: proposalMessage.trim() || null,
      });
      setProposalOpen(false);
      setProposalAmount(''); setProposalMessage(''); setProposalRequiresReturn(false);
    }, 'Proposta inviata al cliente');
  };

  const handleRespond = (proposalId, accept) =>
    run(
      async () => {
        const res = await caseApi.respondProposal(caseId, proposalId, { user_id: user.id, accept });
        if (res?.refund?.status === 'FAILED') {
          throw new Error(`Rimborso non riuscito: ${res.refund.failure_reason || 'errore Stripe'}. Rarifex sta verificando.`);
        }
      },
      accept ? 'Proposta accettata' : 'Proposta rifiutata: la conversazione resta aperta'
    );

  const handleEscalate = () => {
    if (escalateReason.trim().length < 5) {
      toast.error('Spiega brevemente il motivo');
      return;
    }
    run(async () => {
      await caseApi.escalate(caseId, { user_id: user.id, reason: escalateReason.trim() });
      setEscalateOpen(false);
      setEscalateReason('');
    }, 'Rarifex è stato coinvolto');
  };

  const handleReturnShip = () => {
    if (!returnForm.carrier_code || !returnForm.tracking_number.trim()) {
      toast.error('Seleziona il corriere e inserisci il tracking');
      return;
    }
    run(async () => {
      await caseApi.shipReturn(caseId, { user_id: user.id, ...returnForm });
      setReturnOpen(false);
    }, 'Spedizione di reso registrata');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-10 h-10 animate-spin text-shop-primary" />
      </div>
    );
  }
  if (!data) return null;

  const orderItems = data.order?.items || [];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="order-case-page">
      <Helmet><title>{`Assistenza ordine ${data.case_number} · Rarifex`}</title></Helmet>

      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition-colors mb-4"
            data-testid="case-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Torna indietro
          </button>

          <div className="flex flex-col md:flex-row md:items-center gap-4 md:justify-between">
            <div className="flex items-start gap-4">
              <div className={`w-14 h-14 rounded-2xl ${typeMeta.bg} ring-1 ${typeMeta.ring} flex items-center justify-center flex-shrink-0`}>
                <typeMeta.Icon className={`w-7 h-7 ${typeMeta.accent}`} />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">{typeMeta.label}</h1>
                <p className="text-sm text-slate-500 mt-1">
                  <span className="font-mono">{data.case_number}</span> · Ordine{' '}
                  <span className="font-mono">{data.order_number || data.order_id?.slice(0, 8)}</span> ·{' '}
                  Aperto il {formatDate(data.created_at)}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Badge className={`${statusMeta.cls} border`} data-testid="case-status-badge">{statusMeta.label || data.status}</Badge>
              {data.return_status !== 'NOT_APPLICABLE' && (
                <Badge className="bg-teal-50 text-teal-700 border-teal-200 border">
                  {RETURN_STATUS_LABELS[data.return_status]}
                </Badge>
              )}
              {data.refund_status !== 'NONE' && (
                <Badge className="bg-amber-50 text-amber-800 border-amber-200 border">
                  {REFUND_STATUS_LABELS[data.refund_status]}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 grid lg:grid-cols-3 gap-6">
        {/* ------- Colonna principale ------- */}
        <div className="lg:col-span-2 space-y-6">
          {/* Banner collaborativo */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <div className="rounded-3xl bg-gradient-to-br from-shop-primary to-[#00615f] p-6 text-white shadow-xl shadow-shop-primary/20 relative overflow-hidden">
              <Sparkles className="w-32 h-32 absolute -right-6 -top-6 text-white/10" />
              <h2 className="text-lg md:text-lg font-bold mb-2">Cerchiamo prima di risolvere insieme il problema</h2>
              <p className="text-sm text-white/85 leading-relaxed max-w-2xl">
                L'obiettivo di Rarifex è aiutare acquirente e artigiano a trovare rapidamente una soluzione
                soddisfacente per entrambi. Usa questa conversazione per scambiare informazioni, foto e
                proporre una soluzione.
              </p>
            </div>
          </motion.div>

          {/* Problema segnalato */}
          <Card className="p-6">
            <SectionTitle icon={FileText}>Problema segnalato</SectionTitle>
            {data.reason_code && (
              <Badge className="bg-slate-100 text-slate-700 mb-3">{data.reason_code}</Badge>
            )}
            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{data.description}</p>

            {data.external_video_url && (
              <a
                href={data.external_video_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-shop-primary hover:underline mt-4"
              >
                <ExternalLink className="w-4 h-4" /> Guarda il video allegato
              </a>
            )}

            {data.attachments?.length > 0 && (
              <div className="flex flex-wrap gap-3 mt-5">
                {data.attachments.map((a) => (
                  <a key={a.id} href={a.url} target="_blank" rel="noreferrer" className="group">
                    {a.mime_type?.startsWith('image/') ? (
                      <img
                        src={a.url}
                        alt={a.file_name}
                        className="w-24 h-24 rounded-xl object-cover ring-1 ring-slate-200 group-hover:ring-shop-primary/50 group-hover:scale-[1.03] transition-all"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-xl bg-slate-100 flex flex-col items-center justify-center gap-1 ring-1 ring-slate-200">
                        <FileText className="w-6 h-6 text-slate-400" />
                        <span className="text-[10px] text-slate-500 px-1 truncate w-full text-center">{a.file_name}</span>
                      </div>
                    )}
                  </a>
                ))}
              </div>
            )}
          </Card>

          {/* Conversazione */}
          <Card className="p-6">
            <SectionTitle icon={MessageSquare} hint="Messaggi di questa richiesta · la chat completa dell'ordine resta disponibile nei Messaggi">
              Conversazione
            </SectionTitle>

            <div className="space-y-3 max-h-[26rem] overflow-y-auto pr-1" data-testid="case-thread">
              {(data.thread || []).length === 0 && (
                <p className="text-sm text-slate-400 py-6 text-center">Nessun messaggio ancora.</p>
              )}
              {(data.thread || []).map((m) => {
                const system = m.message_type === 'system_case';
                const mine = m.sender_id === user?.id;
                if (system) {
                  return (
                    <div key={m.id} className="flex justify-center">
                      <div className="max-w-[92%] rounded-2xl bg-gradient-to-r from-shop-primary/8 to-[#00CED1]/6 border border-shop-primary/15 px-4 py-3">
                        <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">
                          {m.content.replace(/\*\*/g, '')}
                        </p>
                        <p className="text-[11px] text-slate-400 mt-1.5">Rarifex · {formatDate(m.created_at, true)}</p>
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      mine ? 'bg-shop-primary text-white' : 'bg-slate-100 text-slate-800'
                    }`}>
                      <p className="text-sm whitespace-pre-line leading-relaxed">{m.content}</p>
                      <p className={`text-[11px] mt-1.5 ${mine ? 'text-white/70' : 'text-slate-400'}`}>
                        {m.sender_name} · {formatDate(m.created_at, true)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {active && (
              <div className="mt-5 flex gap-3 items-end">
                <Textarea
                  rows={2}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Scrivi un messaggio…"
                  data-testid="case-message-input"
                />
                <Button
                  onClick={handleSend}
                  disabled={busy || !message.trim()}
                  className="bg-shop-primary hover:bg-shop-primary-dark text-white h-11 px-5"
                  data-testid="case-message-send"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            )}
          </Card>

          {/* Proposte */}
          {(data.proposals?.length > 0 || (isSeller && active)) && (
            <Card className="p-6">
              <SectionTitle icon={Handshake} hint="Chi vende può proporre riparazione, sostituzione o rimborso">
                Proposte di soluzione
              </SectionTitle>

              {isSeller && active && (
                <Button
                  onClick={() => setProposalOpen(true)}
                  className="mb-4 bg-shop-accent hover:bg-[#B8860B] text-white font-semibold"
                  data-testid="propose-solution-btn"
                >
                  <Handshake className="w-4 h-4 mr-2" /> Proponi una soluzione
                </Button>
              )}

              <div className="space-y-3">
                {(data.proposals || []).map((p) => (
                  <ProposalCard key={p.id} proposal={p} isBuyer={isBuyer && active} onRespond={handleRespond} busy={busy} />
                ))}
                {!data.proposals?.length && (
                  <p className="text-sm text-slate-400">Nessuna proposta ancora.</p>
                )}
              </div>
            </Card>
          )}

          {/* Rimborsi */}
          {data.refunds?.length > 0 && (
            <Card className="p-6">
              <SectionTitle icon={BadgeEuro}>Rimborsi</SectionTitle>
              <div className="space-y-3">
                {data.refunds.map((r) => (
                  <div key={r.id} className="flex items-center justify-between gap-3 p-4 rounded-xl bg-slate-50 border border-slate-100">
                    <div>
                      <p className="font-semibold text-slate-800">{formatMoney(r.amount)}</p>
                      <p className="text-xs text-slate-500">
                        {r.reason || '—'} · {formatDate(r.created_at, true)}
                      </p>
                      {r.stripe_refund_reference && (
                        <p className="text-[11px] font-mono text-slate-400 mt-0.5">{r.stripe_refund_reference}</p>
                      )}
                      {r.failure_reason && (
                        <p className="text-xs text-red-600 mt-1">{r.failure_reason}</p>
                      )}
                    </div>
                    <Badge className={
                      r.status === 'SUCCEEDED' ? 'bg-emerald-100 text-emerald-700 border-emerald-200 border'
                      : r.status === 'FAILED' ? 'bg-red-100 text-red-700 border-red-200 border'
                      : 'bg-amber-100 text-amber-800 border-amber-200 border'
                    }>
                      {{ REQUESTED: 'Richiesto', APPROVED: 'Approvato', PROCESSING: 'In corso',
                         SUCCEEDED: 'Completato', FAILED: 'Non riuscito', CANCELLED: 'Annullato' }[r.status]}
                    </Badge>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Timeline */}
          <Card className="p-6">
            <SectionTitle icon={Clock}>Cronologia del caso</SectionTitle>
            <div className="space-y-4">
              {(data.timeline || []).map((t, i) => {
                const meta = TIMELINE_META[t.action] || { label: t.action, Icon: Sparkles, cls: 'text-slate-500 bg-slate-100' };
                return (
                  <motion.div
                    key={t.id}
                    initial={{ opacity: 0, x: -6 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.03 }}
                    className="flex gap-3"
                  >
                    <div className={`w-8 h-8 rounded-full ${meta.cls} flex items-center justify-center flex-shrink-0`}>
                      <meta.Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 pb-1 border-b border-slate-50 last:border-0">
                      <p className="text-sm font-medium text-slate-700">{meta.label}</p>
                      <p className="text-xs text-slate-400">
                        {formatDate(t.created_at, true)}
                        {t.actor_type ? ` · ${{ BUYER: 'Cliente', SELLER: 'Artigiano', SUPERADMIN: 'Rarifex', SYSTEM: 'Sistema' }[t.actor_type]}` : ''}
                        {t.metadata?.amount ? ` · ${formatMoney(t.metadata.amount)}` : ''}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </Card>
        </div>

        {/* ------- Colonna laterale ------- */}
        <div className="space-y-6">
          {/* Azioni */}
          {active && (
            <Card className="p-5">
              <h3 className="text-sm font-semibold text-slate-800 mb-4">Azioni disponibili</h3>
              <div className="space-y-2.5">
                {isSeller && (
                  <>
                    <Button
                      onClick={() => setProposalOpen(true)}
                      className="w-full bg-shop-accent hover:bg-[#B8860B] text-white font-semibold"
                      data-testid="side-propose-btn"
                    >
                      <Handshake className="w-4 h-4 mr-2" /> Proponi una soluzione
                    </Button>
                    {data.return_status === 'RETURN_REQUESTED' && (
                      <Button
                        variant="outline"
                        className="w-full border-teal-300 text-teal-700 hover:bg-teal-50"
                        onClick={() => run(() => caseApi.authorizeReturn(caseId, user.id), 'Reso autorizzato')}
                        disabled={busy}
                        data-testid="authorize-return-btn"
                      >
                        <Undo2 className="w-4 h-4 mr-2" /> Autorizza il reso
                      </Button>
                    )}
                    {['RETURN_IN_TRANSIT', 'RETURN_AUTHORIZED'].includes(data.return_status) && (
                      <Button
                        variant="outline"
                        className="w-full border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        onClick={() => run(() => caseApi.returnReceived(caseId, user.id), 'Reso ricevuto')}
                        disabled={busy}
                        data-testid="return-received-btn"
                      >
                        <Package className="w-4 h-4 mr-2" /> Ho ricevuto il reso
                      </Button>
                    )}
                  </>
                )}

                {isBuyer && data.return_status === 'RETURN_AUTHORIZED' && (
                  <Button
                    className="w-full bg-shop-primary hover:bg-shop-primary-dark text-white font-semibold"
                    onClick={() => setReturnOpen(true)}
                    data-testid="ship-return-btn"
                  >
                    <Truck className="w-4 h-4 mr-2" /> Registra la spedizione del reso
                  </Button>
                )}

                {data.status !== 'ESCALATED_TO_RARIFEX' && (
                  <Button
                    variant="outline"
                    className="w-full border-slate-300"
                    onClick={() => setEscalateOpen(true)}
                    data-testid="escalate-btn"
                  >
                    <LifeBuoy className="w-4 h-4 mr-2" /> Chiedi aiuto a Rarifex
                  </Button>
                )}

                {isBuyer && data.refund_status === 'NONE' && (
                  <Button
                    variant="ghost"
                    className="w-full text-slate-500 hover:text-slate-700"
                    onClick={() => run(() => caseApi.cancel(caseId, user.id), 'Richiesta annullata')}
                    disabled={busy}
                    data-testid="cancel-case-btn"
                  >
                    <Ban className="w-4 h-4 mr-2" /> Annulla la richiesta
                  </Button>
                )}
              </div>
            </Card>
          )}

          {/* Ordine */}
          <Card className="p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-4">Ordine</h3>
            <div className="space-y-3">
              {orderItems.slice(0, 4).map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl overflow-hidden bg-slate-100 flex-shrink-0">
                    {item.image ? (
                      <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><Package className="w-5 h-5 text-slate-300" /></div>
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-700 truncate">{item.title}</p>
                    <p className="text-xs text-slate-500">Qtà {item.quantity} · {formatMoney(item.totalPrice * item.quantity)}</p>
                  </div>
                </div>
              ))}
              {!orderItems.length && (
                <p className="text-sm text-slate-600">{data.order?.title}</p>
              )}
            </div>

            <div className="mt-4 pt-4 border-t border-slate-100 space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Totale ordine</span><span className="font-semibold">{formatMoney(data.order_amount)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Già rimborsato</span><span>{formatMoney(data.refunded_amount)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Rimborsabile</span><span className="font-semibold text-shop-primary">{formatMoney(data.refundable_amount)}</span></div>
            </div>

            <div className="mt-4 pt-4 border-t border-slate-100 text-xs text-slate-500 space-y-1">
              <p>Cliente: <span className="text-slate-700">{data.buyer_name}</span></p>
              <p>Artigiano: <span className="text-slate-700">{data.seller_name}</span></p>
              {data.seller_pays_return && (
                <p className="text-teal-700 pt-1">Spedizione di reso a carico dell'artigiano</p>
              )}
            </div>

            <Link
              to={`/messages?crafter=${isBuyer ? data.seller_id : data.buyer_id}`}
              className="inline-flex items-center gap-1.5 text-xs text-shop-primary hover:underline mt-4"
            >
              <MessageSquare className="w-3.5 h-3.5" /> Apri la chat completa
            </Link>
          </Card>

          {/* Spedizione */}
          <Card className="p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">Spedizioni dell'ordine</h3>
            <ShipmentTimeline orderId={data.order_id} compact />
          </Card>
        </div>
      </div>

      {/* Dialog: proponi soluzione */}
      <Dialog open={proposalOpen} onOpenChange={setProposalOpen}>
        <DialogContent className="max-w-lg" data-testid="proposal-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Proponi una soluzione</DialogTitle>
            <DialogDescription>
              Importo ordine {formatMoney(data.order_amount)} · rimborsabile {formatMoney(data.refundable_amount)}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Tipo di soluzione</Label>
              <Select value={proposalType} onValueChange={setProposalType}>
                <SelectTrigger data-testid="proposal-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PROPOSAL_TYPE_META).map(([key, m]) => (
                    <SelectItem key={key} value={key}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {proposalType === 'PARTIAL_REFUND' && (
              <div className="space-y-2">
                <Label htmlFor="proposal-amount">Importo del rimborso (€)</Label>
                <Input
                  id="proposal-amount"
                  type="number"
                  min="0"
                  step="0.01"
                  max={data.refundable_amount}
                  value={proposalAmount}
                  onChange={(e) => setProposalAmount(e.target.value)}
                  data-testid="proposal-amount-input"
                />
                <p className="text-xs text-slate-500">Massimo {formatMoney(data.refundable_amount)}</p>
              </div>
            )}

            {proposalType === 'FULL_REFUND' && (
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm text-slate-600">
                Verrà proposto un rimborso di <strong>{formatMoney(data.refundable_amount)}</strong>.
              </div>
            )}

            {['PARTIAL_REFUND', 'FULL_REFUND'].includes(proposalType) && (() => {
              const proposed = proposalType === 'FULL_REFUND'
                ? Number(data.refundable_amount || 0)
                : (parseFloat(proposalAmount) || 0);
              if (!proposed || proposed <= 0) return null;
              const collected = Number(data.order_amount || 0);
              const remaining = Math.max(0, collected - Number(data.refunded_amount || 0) - proposed);
              return (
                <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm" data-testid="proposal-balance-impact">
                  <div className="flex items-center gap-2 font-semibold text-amber-900 mb-1">
                    <BadgeEuro className="w-4 h-4" /> Impatto sul tuo saldo
                  </div>
                  <p className="text-amber-800 leading-relaxed">
                    Rimborserai <strong>{formatMoney(proposed)}</strong> dei <strong>{formatMoney(collected)}</strong> incassati per questo ordine.{' '}
                    Resteranno <strong>{formatMoney(remaining)}</strong> sul tuo saldo.
                  </p>
                </div>
              );
            })()}

            <div className="space-y-2">
              <Label htmlFor="proposal-message">Motivazione / messaggio</Label>
              <Textarea
                id="proposal-message"
                rows={3}
                value={proposalMessage}
                onChange={(e) => setProposalMessage(e.target.value)}
                placeholder="Spiega al cliente la tua proposta…"
                data-testid="proposal-message-input"
              />
            </div>

            {['PARTIAL_REFUND', 'FULL_REFUND'].includes(proposalType) && (
              <label className="flex items-center gap-2.5 text-sm text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={proposalRequiresReturn}
                  onChange={(e) => setProposalRequiresReturn(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-shop-primary"
                  data-testid="proposal-requires-return"
                />
                Il rimborso avverrà dopo il rientro del prodotto
              </label>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => setProposalOpen(false)}>Annulla</Button>
            <Button
              className="flex-1 bg-shop-accent hover:bg-[#B8860B] text-white font-semibold"
              onClick={handleProposal}
              disabled={busy}
              data-testid="proposal-submit-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Invia proposta
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog: escalation */}
      <Dialog open={escalateOpen} onOpenChange={setEscalateOpen}>
        <DialogContent className="max-w-lg" data-testid="escalate-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Chiedi aiuto a Rarifex</DialogTitle>
          </DialogHeader>
          <div className="rounded-2xl bg-amber-50 border border-amber-100 p-4 flex gap-3">
            <ShieldCheck className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-900 leading-relaxed">
              Prima di coinvolgere Rarifex, prova a trovare una soluzione direttamente con l'altra parte.
              Nella maggior parte dei casi è il modo più veloce per risolvere il problema.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="escalate-reason">Perché hai bisogno del nostro aiuto?</Label>
            <Textarea
              id="escalate-reason"
              rows={4}
              value={escalateReason}
              onChange={(e) => setEscalateReason(e.target.value)}
              placeholder="Raccontaci cosa non riuscite a risolvere…"
              data-testid="escalate-reason-input"
            />
          </div>
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={() => setEscalateOpen(false)}>
              Continuo a provare
            </Button>
            <Button
              className="flex-1 bg-slate-900 hover:bg-slate-800 text-white font-semibold"
              onClick={handleEscalate}
              disabled={busy}
              data-testid="escalate-submit-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Coinvolgi Rarifex
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog: spedizione reso */}
      <Dialog open={returnOpen} onOpenChange={setReturnOpen}>
        <DialogContent className="max-w-lg" data-testid="return-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Registra la spedizione del reso</DialogTitle>
            <DialogDescription>
              Il tracking del reso viene seguito con lo stesso sistema delle spedizioni Rarifex.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Corriere</Label>
              <Select
                value={returnForm.carrier_code}
                onValueChange={(v) => setReturnForm((f) => ({ ...f, carrier_code: v }))}
              >
                <SelectTrigger data-testid="return-carrier-select"><SelectValue placeholder="Seleziona il corriere" /></SelectTrigger>
                <SelectContent>
                  {carriers.map((c) => (
                    <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="return-tracking">Numero di tracking</Label>
              <Input
                id="return-tracking"
                value={returnForm.tracking_number}
                onChange={(e) => setReturnForm((f) => ({ ...f, tracking_number: e.target.value }))}
                data-testid="return-tracking-input"
              />
            </div>

            {returnForm.carrier_code === 'other' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="return-carrier-name">Nome del corriere</Label>
                  <Input
                    id="return-carrier-name"
                    value={returnForm.custom_carrier_name}
                    onChange={(e) => setReturnForm((f) => ({ ...f, custom_carrier_name: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="return-tracking-url">Link di tracking (https://…)</Label>
                  <Input
                    id="return-tracking-url"
                    value={returnForm.custom_tracking_url}
                    onChange={(e) => setReturnForm((f) => ({ ...f, custom_tracking_url: e.target.value }))}
                  />
                </div>
              </>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => setReturnOpen(false)}>Annulla</Button>
            <Button
              className="flex-1 bg-shop-primary hover:bg-shop-primary-dark text-white font-semibold"
              onClick={handleReturnShip}
              disabled={busy}
              data-testid="return-submit-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Registra spedizione
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
